<template>
  <div class="training-manager">
    <section class="recent-courses" aria-labelledby="recent-courses-title">
      <header class="recent-courses__header">
        <div>
          <span class="training-manager__eyebrow">GRUPPENKURSE</span>
          <h3 id="recent-courses-title">Zuletzt besucht</h3>
          <p>Die letzten vier Gruppenkurse, bei denen die Person als anwesend erfasst wurde.</p>
        </div>
        <span class="recent-courses__icon" aria-hidden="true">
          <v-icon>mdi-account-group-outline</v-icon>
        </span>
      </header>

      <div v-if="loadingCourses" class="recent-courses__state" aria-live="polite">
        <v-progress-circular indeterminate color="primary" :size="30" :width="3" />
        <span>Letzte Gruppenkurse werden geladen</span>
      </div>

      <div v-else-if="recentCourses.length" class="recent-courses__grid">
        <article v-for="(course, index) in recentCourses" :key="course.course._id || `${course.time}-${index}`" class="recent-course">
          <div class="recent-course__image">
            <img v-if="courseImage(course)" :src="courseImage(course)" :alt="courseName(course)" loading="lazy" />
            <v-icon v-else size="34">mdi-image-off-outline</v-icon>
          </div>
          <div class="recent-course__copy">
            <strong>{{ courseName(course) }}</strong>
            <span>
              <v-icon x-small>mdi-calendar-check-outline</v-icon>
              {{ course.time || "Zeitpunkt unbekannt" }}
            </span>
          </div>
        </article>
      </div>

      <div v-else class="recent-courses__state recent-courses__state--empty">
        <v-icon>mdi-calendar-blank-outline</v-icon>
        <span>{{ coursesLoadFailed ? "Gruppenkurse konnten nicht geladen werden." : "Noch keine besuchten Gruppenkurse vorhanden." }}</span>
        <v-btn v-if="coursesLoadFailed" small text color="primary" class="text-none" @click="loadRecentCourses()"> Erneut versuchen </v-btn>
      </div>
    </section>

    <div class="training-manager__divider"></div>

    <header class="training-manager__header">
      <div class="training-manager__title">
        <span class="training-manager__eyebrow">TRAININGSBETREUUNG</span>
        <h3>Trainingspläne</h3>
        <p>Neue Split-Pläne und klassische Trainingspläne gemeinsam verwalten.</p>
      </div>

      <div class="training-manager__header-actions">
        <v-btn
          v-if="canImportSplits && currentV2Plan"
          outlined
          color="primary"
          class="text-none font-weight-bold training-manager__import"
          :disabled="!hasImportableOldSplits"
          :title="hasImportableOldSplits ? 'Splits aus früheren Split-Plänen übernehmen' : 'Keine früheren Split-Pläne mit Splits vorhanden'"
          @click="showOldPlansDialog = true">
          <v-icon left>mdi-content-copy</v-icon>
          Splits übernehmen
        </v-btn>
        <v-btn color="primary" depressed class="text-none font-weight-bold training-manager__create" @click="createNewPlan">
          <v-icon left>mdi-plus</v-icon>
          Neuen Plan erstellen
        </v-btn>
      </div>
    </header>

    <div v-if="loadingPlans" class="training-manager__state">
      <v-progress-circular indeterminate color="primary" :size="48" :width="5" />
      <strong>Trainingspläne werden geladen</strong>
    </div>

    <div v-else-if="!combinedPlans.length" class="training-manager__state">
      <span class="training-manager__state-icon">
        <v-icon size="38">mdi-dumbbell</v-icon>
      </span>
      <strong>Noch kein Trainingsplan vorhanden</strong>
      <p>Erstelle den ersten Plan direkt für {{ customerName }}.</p>
      <v-btn color="primary" depressed class="text-none font-weight-bold mt-2" @click="createNewPlan"> Trainingsplan erstellen </v-btn>
    </div>

    <template v-else>
      <div class="training-manager__toolbar">
        <div class="training-manager__legend">
          <span><i class="training-manager__dot training-manager__dot--new"></i> Split-basiert</span>
          <span><i class="training-manager__dot training-manager__dot--classic"></i> Klassisch</span>
        </div>
        <v-switch
          v-if="deletedPlanCount"
          v-model="showDeleted"
          dense
          hide-details
          color="primary"
          class="training-manager__deleted-switch"
          :label="`Gelöschte anzeigen (${deletedPlanCount})`" />
      </div>

      <div class="training-manager__list">
        <article
          v-for="plan in visiblePlans"
          :key="plan.id"
          class="training-plan"
          :class="{
            'training-plan--classic': plan.type === 'old',
            'training-plan--deleted': plan.deleted,
          }">
          <div class="training-plan__accent"></div>
          <div class="training-plan__icon">
            <v-icon>{{ plan.type === "new" ? "mdi-view-grid-plus-outline" : "mdi-clipboard-text-outline" }}</v-icon>
          </div>

          <div class="training-plan__content">
            <div class="training-plan__heading">
              <div>
                <div class="training-plan__labels">
                  <span :class="`training-plan__type training-plan__type--${plan.type}`">
                    {{ plan.type === "new" ? "Split-basiert" : "Klassisch" }}
                  </span>
                  <span v-if="plan.deleted" class="training-plan__type training-plan__type--deleted">Gelöscht</span>
                </div>
                <h4>{{ plan.title }}</h4>
              </div>
              <v-btn
                v-if="plan.type === 'new' && plan.splits.length"
                icon
                small
                :aria-label="plan.isExpanded ? 'Split-Details schliessen' : 'Split-Details öffnen'"
                @click="toggleExpand(plan)">
                <v-icon>{{ plan.isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </v-btn>
            </div>

            <div class="training-plan__meta">
              <span>
                <v-icon x-small>mdi-calendar-outline</v-icon>
                {{ formatDate(plan.createdDate) }}
              </span>
              <span>
                <v-icon x-small>mdi-account-outline</v-icon>
                {{ plan.instructor || "Unbekannt" }}
              </span>
              <span v-if="plan.type === 'new' && plan.splits.length">
                <v-icon x-small>mdi-view-grid-outline</v-icon>
                {{ plan.splits.length }} {{ plan.splits.length === 1 ? "Split" : "Splits" }}
              </span>
            </div>

            <div v-if="plan.allContributors.length" class="training-plan__contributors">
              <span>Beteiligte:</span>
              {{ contributorNames(plan.allContributors) }}
            </div>

            <v-expand-transition>
              <div v-if="plan.type === 'new' && plan.isExpanded" class="training-plan__splits">
                <div v-for="(split, splitIndex) in plan.splits" :key="`${plan.id}-split-${splitIndex}`" class="training-split">
                  <div>
                    <strong>{{ split.title || `Split ${splitIndex + 1}` }}</strong>
                    <span v-if="split.creatorInfo"> von {{ split.creatorInfo.vorname }} {{ split.creatorInfo.nachname }} </span>
                  </div>
                  <span>{{ Array.isArray(split.exercises) ? split.exercises.length : 0 }} Übungen</span>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div class="training-plan__actions">
            <v-btn v-if="plan.type === 'new' && !plan.deleted" small text color="primary" class="text-none" @click="copyPlan(plan)">
              <v-icon left small>mdi-content-copy</v-icon>
              Kopieren
            </v-btn>
            <v-btn v-if="!plan.deleted" small text color="primary" class="text-none" @click="editPlan(plan)">
              <v-icon left small>mdi-pencil-outline</v-icon>
              Bearbeiten
            </v-btn>
            <v-btn v-if="!plan.deleted" small text color="primary" class="text-none" @click="printPlan(plan)">
              <v-icon left small>mdi-printer-outline</v-icon>
              Drucken
            </v-btn>
            <v-btn
              v-if="plan.type === 'new' && !plan.deleted"
              small
              text
              color="error"
              class="text-none"
              :loading="busyPlanId === plan.id"
              @click="promptDeletePlan(plan)">
              <v-icon left small>mdi-delete-outline</v-icon>
              Löschen
            </v-btn>
            <v-btn
              v-if="plan.type === 'new' && plan.deleted"
              small
              text
              color="primary"
              class="text-none"
              :loading="busyPlanId === plan.id"
              @click="restorePlan(plan)">
              <v-icon left small>mdi-backup-restore</v-icon>
              Wiederherstellen
            </v-btn>
          </div>
        </article>
      </div>
    </template>

    <v-dialog v-model="showDeleteDialog" max-width="470" content-class="training-delete-dialog">
      <v-card v-if="planToDelete" dark class="training-dialog-card">
        <div class="training-dialog-card__icon training-dialog-card__icon--danger">
          <v-icon size="32">mdi-delete-alert-outline</v-icon>
        </div>
        <h3>Trainingsplan löschen?</h3>
        <p>„{{ planToDelete.title }}“ wird ausgeblendet und kann später wiederhergestellt werden.</p>
        <div class="training-dialog-card__actions">
          <v-btn text class="text-none" @click="closeDeleteDialog">Abbrechen</v-btn>
          <v-btn color="error" depressed class="text-none font-weight-bold" :loading="busyPlanId === planToDelete.id" @click="confirmDeletePlan">
            Löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPrintSplitDialog" max-width="500" content-class="training-print-dialog">
      <v-card dark class="training-dialog-card training-dialog-card--list">
        <div class="training-dialog-card__icon">
          <v-icon size="30">mdi-printer-outline</v-icon>
        </div>
        <h3>Split auswählen</h3>
        <p>Welcher Teil des Trainingsplans soll heruntergeladen werden?</p>
        <button
          v-for="(split, index) in printableSplits"
          :key="`print-split-${index}`"
          type="button"
          class="training-print-option"
          @click="downloadSplit(index)">
          <span>
            <strong>{{ split.title || `Split ${index + 1}` }}</strong>
            <small>{{ Array.isArray(split.exercises) ? split.exercises.length : 0 }} Übungen</small>
          </span>
          <v-icon>mdi-download-outline</v-icon>
        </button>
        <div class="training-dialog-card__actions">
          <v-btn text class="text-none" @click="showPrintSplitDialog = false">Schliessen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <OldPlansManagerDialog
      v-if="canImportSplits && currentV2Plan"
      :value="showOldPlansDialog"
      :current-plan="currentV2Plan"
      :old-plans="previousV2Plans"
      access-mode="trainer"
      @close="showOldPlansDialog = false"
      @plan-updated="handleSplitImported" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import OldPlansManagerDialog from "@/views/workouts/v2/OldPlansManagerDialog.vue"
import { mapActions, mapGetters } from "vuex"

export default {
  name: "UserDashboardTrainingPlans",
  components: {
    OldPlansManagerDialog,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingCourses: false,
      recentCourses: [],
      coursesLoadFailed: false,
      loadingPlans: false,
      combinedPlans: [],
      showDeleted: false,
      showDeleteDialog: false,
      planToDelete: null,
      busyPlanId: null,
      showPrintSplitDialog: false,
      printableSplits: [],
      selectedPlanForPrint: null,
      showOldPlansDialog: false,
    }
  },
  computed: {
    ...mapGetters("deviceStore", ["exerciseList"]),
    customerName() {
      return `${this.user.vorname || ""} ${this.user.nachname || ""}`.trim() || "diesen Kunden"
    },
    deletedPlanCount() {
      return this.combinedPlans.filter((plan) => plan.deleted).length
    },
    visiblePlans() {
      return this.combinedPlans.filter((plan) => this.showDeleted || !plan.deleted)
    },
    canImportSplits() {
      return userService.hasRight("tp_create")
    },
    activeV2Plans() {
      return this.combinedPlans.filter((plan) => plan.type === "new" && !plan.deleted && plan.originalObject)
    },
    currentV2Plan() {
      return this.activeV2Plans.length ? this.activeV2Plans[0].originalObject : null
    },
    previousV2Plans() {
      return this.activeV2Plans.slice(1).map((plan) => plan.originalObject)
    },
    hasImportableOldSplits() {
      return this.previousV2Plans.some((plan) => Array.isArray(plan?.splits?.workouts) && plan.splits.workouts.length > 0)
    },
  },
  watch: {
    "user._id": {
      immediate: true,
      handler(userId) {
        this.recentCourses = []
        this.coursesLoadFailed = false
        this.combinedPlans = []
        this.showOldPlansDialog = false
        if (userId) {
          this.loadRecentCourses(userId)
          this.loadPlans(userId)
        }
      },
    },
  },
  methods: {
    ...mapActions("workingoutStore", ["setEditInStore"]),
    ...mapActions("runWorkoutStore", ["setPlanForEditing", "setPlanForCloning"]),
    courseName(entry) {
      return String(entry?.course?.name || "Gruppenkurs")
    },
    courseImage(entry) {
      const image = String(entry?.course?.bild || "")
        .trim()
        .replace(/^\/+/, "")
      return image ? `/img/kurse/${image}` : ""
    },
    async loadRecentCourses(userId = this.user?._id) {
      const requestedUserId = String(userId || "")
      if (!requestedUserId) return

      this.loadingCourses = true
      this.coursesLoadFailed = false
      try {
        const response = await api.getCustomerDashboardTraining(requestedUserId)
        if (String(this.user?._id || "") !== requestedUserId) return
        this.recentCourses = (Array.isArray(response?.courses) ? response.courses : []).filter((entry) => entry && entry.course).slice(0, 4)
      } catch (error) {
        if (String(this.user?._id || "") !== requestedUserId) return
        this.recentCourses = []
        this.coursesLoadFailed = true
      } finally {
        if (String(this.user?._id || "") === requestedUserId) this.loadingCourses = false
      }
    },
    formatDate(value) {
      const date = this.$moment(value)
      return date.isValid() ? date.format("DD.MM.YYYY") : "Datum unbekannt"
    },
    contributorNames(contributors) {
      return contributors
        .map((contributor) => `${contributor?.vorname || ""} ${contributor?.nachname || ""}`.trim())
        .filter(Boolean)
        .join(", ")
    },
    toggleExpand(plan) {
      this.$set(plan, "isExpanded", !plan.isExpanded)
    },
    async loadPlans(userId = this.user?._id) {
      const requestedUserId = String(userId || "")
      if (!requestedUserId) return

      this.loadingPlans = true
      try {
        const [newPlansResponse, oldPlansResponse] = await Promise.all([
          api.workoutApi.fetchCustomerWorkouts(requestedUserId).catch(() => []),
          api.getTrainingPlansForUser(requestedUserId).catch(() => []),
        ])

        if (String(this.user?._id || "") !== requestedUserId) return

        const newPlans = (Array.isArray(newPlansResponse) ? newPlansResponse : []).map((plan) => {
          const splits = Array.isArray(plan?.splits?.workouts) ? plan.splits.workouts : []
          const splitTitles = splits.map((split) => split?.title).filter(Boolean)
          return {
            id: plan._id,
            title: splitTitles.length ? splitTitles.join(", ") : plan.title || "Trainingsplan",
            createdDate: plan.createdAt || plan.updatedAt,
            type: "new",
            deleted: Boolean(plan.deleted),
            originalObject: plan,
            instructor: plan.instructor_id
              ? `${plan.instructor_id.vorname || ""} ${plan.instructor_id.nachname || ""}`.trim()
              : plan.form?.instructor || "Unbekannt",
            allContributors: Array.isArray(plan.allContributors) ? plan.allContributors : [],
            splits,
            isExpanded: false,
          }
        })

        const oldPlans = (Array.isArray(oldPlansResponse) ? oldPlansResponse : []).map((plan) => ({
          id: plan._id,
          title: plan.form?.title || "Klassischer Trainingsplan",
          createdDate: plan.form?.ts ? new Date(plan.form.ts * 1000).toISOString() : plan.createdAt,
          type: "old",
          deleted: false,
          instructor: plan.creatorInfo?.vorname
            ? `${plan.creatorInfo.vorname || ""} ${plan.creatorInfo.nachname || ""}`.trim()
            : plan.form?.instructor || "Unbekannt",
          allContributors: [],
          splits: [],
          isExpanded: false,
          originalObject: plan,
        }))

        this.combinedPlans = [...newPlans, ...oldPlans].sort(
          (first, second) => this.$moment(second.createdDate).valueOf() - this.$moment(first.createdDate).valueOf(),
        )
      } catch (error) {
        if (String(this.user?._id || "") !== requestedUserId) return
        this.combinedPlans = []
        this.$toast.error("Trainingspläne konnten nicht geladen werden")
      } finally {
        if (String(this.user?._id || "") === requestedUserId) {
          this.loadingPlans = false
          this.$emit("loaded")
        }
      }
    },
    async handleSplitImported() {
      this.showOldPlansDialog = false
      this.$toast.success("Split wurde zum aktuellen Plan hinzugefügt")
      await this.loadPlans()
    },
    createNewPlan() {
      this.$router.push(`/workouts/createnew?userId=${this.user._id}`)
    },
    copyPlan(plan) {
      this.setPlanForCloning({ form: plan.originalObject })
      this.$router.push({ name: "WorkoutCreateNew", params: { isClone: "true" } })
    },
    editPlan(plan) {
      if (plan.type === "new") {
        this.setPlanForEditing({ form: plan.originalObject })
        this.$router.push({ name: "WorkoutCreateNew", params: { id: plan.id } })
        return
      }
      this.editClassicPlan(plan.originalObject)
    },
    editClassicPlan(sourcePlan) {
      const plan = JSON.parse(JSON.stringify(sourcePlan))
      const exercises = Array.isArray(plan.exercises) ? plan.exercises : []
      const availableExercises = Array.isArray(this.exerciseList) ? this.exerciseList : []

      exercises.forEach((exercise) => {
        if (exercise.exercise_id?._id) exercise.exercise_id = exercise.exercise_id._id
        const original = availableExercises.find((entry) => String(entry._id) === String(exercise.exercise_id))
        if (!original) return

        if (original.title) exercise.title = original.title
        if (original.picture) exercise.picture = original.picture
        if (original.manual) exercise.manual = original.manual
        if (original.set_exercise_settings) exercise.set_exercise_settings = original.set_exercise_settings

        const settings = Array.isArray(original.set_exercise_settings) ? original.set_exercise_settings : []
        ;(Array.isArray(exercise.sets) ? exercise.sets : []).forEach((set) => {
          set.active = false
          set.activeBefore = false
          if (!Array.isArray(set.settings)) set.settings = []
          settings.forEach((setting) => {
            if (!set.settings.find((existing) => String(existing._id) === String(setting._id))) {
              set.settings.push(setting)
            }
          })
        })
      })

      this.setEditInStore(plan)
      this.$router.push(`/workouts/edit/${plan._id}`)
    },
    printPlan(plan) {
      if (plan.type === "new") {
        const splits = Array.isArray(plan.originalObject?.splits?.workouts) ? plan.originalObject.splits.workouts : []
        if (!splits.length) {
          this.$toast.error("Dieser Plan enthält keine druckbaren Splits")
          return
        }
        this.selectedPlanForPrint = plan.originalObject
        this.printableSplits = splits
        this.showPrintSplitDialog = true
        return
      }

      this.$toast.info("Download wird vorbereitet")
      api.fetchPlanPDF(plan.originalObject).catch(() => {
        this.$toast.error("Trainingsplan konnte nicht gedruckt werden")
      })
    },
    downloadSplit(index) {
      if (!this.selectedPlanForPrint?._id) return
      this.$toast.info("Download wird vorbereitet")
      api.downloadSplitPDF(this.selectedPlanForPrint._id, index, "trainer")
      this.showPrintSplitDialog = false
    },
    promptDeletePlan(plan) {
      this.planToDelete = plan
      this.showDeleteDialog = true
    },
    closeDeleteDialog() {
      if (this.busyPlanId) return
      this.showDeleteDialog = false
      this.planToDelete = null
    },
    async confirmDeletePlan() {
      if (!this.planToDelete) return
      this.busyPlanId = this.planToDelete.id
      try {
        await api.workoutApi.deleteWorkoutV2(this.planToDelete.id, "trainer")
        this.$toast.success("Trainingsplan wurde gelöscht")
        this.showDeleteDialog = false
        this.planToDelete = null
        await this.loadPlans()
      } catch (error) {
        this.$toast.error("Trainingsplan konnte nicht gelöscht werden")
      } finally {
        this.busyPlanId = null
      }
    },
    async restorePlan(plan) {
      this.busyPlanId = plan.id
      try {
        await api.workoutApi.restoreWorkoutV2(plan.id, "trainer")
        this.$toast.success("Trainingsplan wurde wiederhergestellt")
        await this.loadPlans()
      } catch (error) {
        this.$toast.error("Trainingsplan konnte nicht wiederhergestellt werden")
      } finally {
        this.busyPlanId = null
      }
    },
  },
}
</script>

<style scoped>
.training-manager {
  padding: 20px 24px 4px;
  color: #f4f7f5;
}

.recent-courses {
  padding: 4px 0 22px;
}

.recent-courses__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
}

.recent-courses__header h3 {
  margin: 4px 0;
  color: #ffffff;
  font-size: 24px;
  letter-spacing: -0.03em;
}

.recent-courses__header p {
  margin: 0;
  color: #a6ada8;
  font-size: 13px;
}

.recent-courses__icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  color: #7fd7a1;
  background: rgba(0, 150, 64, 0.13);
}

.recent-courses__icon .v-icon {
  color: inherit !important;
}

.recent-courses__grid {
  display: grid;
  gap: 11px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.recent-course {
  min-width: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.026);
}

.recent-course__image {
  position: relative;
  display: flex;
  height: 112px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #718078;
  background: radial-gradient(circle at 85% 5%, rgba(0, 150, 64, 0.2), transparent 48%), #171b19;
}

.recent-course__image::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 45%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.52));
  content: "";
  pointer-events: none;
}

.recent-course__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.recent-course__image .v-icon {
  color: inherit !important;
}

.recent-course__copy {
  display: flex;
  min-height: 76px;
  flex-direction: column;
  gap: 7px;
  padding: 12px 13px 13px;
}

.recent-course__copy strong {
  overflow: hidden;
  color: #f6f8f7;
  font-size: 14px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-course__copy span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #9fa8a2;
  font-size: 12px;
  line-height: 1.4;
}

.recent-course__copy .v-icon {
  flex: 0 0 auto;
  color: #65ca8b !important;
}

.recent-courses__state {
  display: flex;
  min-height: 112px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  color: #9fa8a2;
  background: rgba(255, 255, 255, 0.018);
  font-size: 13px;
  text-align: center;
}

.recent-courses__state--empty {
  flex-wrap: wrap;
}

.recent-courses__state--empty > .v-icon {
  color: #748078 !important;
}

.training-manager__divider {
  height: 1px;
  margin: 0 0 22px;
  background: rgba(255, 255, 255, 0.08);
}

.training-manager__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  padding: 4px 0 20px;
}

.training-manager__eyebrow {
  color: #65ca8b;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.11em;
}

.training-manager__title h3 {
  margin: 4px 0;
  color: #ffffff;
  font-size: 24px;
  letter-spacing: -0.03em;
}

.training-manager__title p,
.training-manager__state p {
  margin: 0;
  color: #a6ada8;
  font-size: 13px;
}

.training-manager__header-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 14px;
}

.training-manager__create,
.training-manager__import {
  min-height: 44px;
  border-radius: 12px;
}

.training-manager__state {
  display: flex;
  min-height: 330px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.13);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.018);
  text-align: center;
}

.training-manager__state strong {
  color: #ffffff;
  font-size: 17px;
}

.training-manager__state-icon {
  display: inline-flex;
  width: 62px;
  height: 62px;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  border-radius: 18px;
  color: #65ca8b;
  background: rgba(0, 150, 64, 0.13);
}

.training-manager__state-icon .v-icon {
  color: inherit !important;
}

.training-manager__toolbar {
  display: flex;
  min-height: 46px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
  padding: 0 4px;
}

.training-manager__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.training-manager__legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #aeb5b0;
  font-size: 12px;
}

.training-manager__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.training-manager__dot--new {
  background: #36bc72;
}

.training-manager__dot--classic {
  background: #5aa6e8;
}

.training-manager__deleted-switch {
  margin: 0;
}

.training-manager__deleted-switch ::v-deep .v-label {
  color: #aeb5b0 !important;
  font-size: 12px;
}

.training-manager__list {
  display: grid;
  gap: 11px;
}

.training-plan {
  position: relative;
  display: grid;
  min-height: 126px;
  align-items: center;
  gap: 16px;
  padding: 17px 18px 17px 21px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 17px;
  background: radial-gradient(circle at 98% 0%, rgba(0, 150, 64, 0.09), transparent 32%), rgba(255, 255, 255, 0.026);
  grid-template-columns: 48px minmax(0, 1fr) auto;
}

.training-plan--classic {
  background: radial-gradient(circle at 98% 0%, rgba(58, 144, 222, 0.09), transparent 32%), rgba(255, 255, 255, 0.026);
}

.training-plan--deleted {
  opacity: 0.68;
}

.training-plan__accent {
  position: absolute;
  top: 16px;
  bottom: 16px;
  left: 0;
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: #35bb70;
}

.training-plan--classic .training-plan__accent {
  background: #5aa6e8;
}

.training-plan__icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  color: #71d899;
  background: rgba(0, 150, 64, 0.13);
}

.training-plan--classic .training-plan__icon {
  color: #80bdf0;
  background: rgba(58, 144, 222, 0.13);
}

.training-plan__icon .v-icon {
  color: inherit !important;
}

.training-plan__content {
  min-width: 0;
}

.training-plan__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.training-plan__heading h4 {
  margin: 5px 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 17px;
  letter-spacing: -0.02em;
  line-height: 1.3;
  text-overflow: ellipsis;
}

.training-plan__heading .v-btn .v-icon {
  color: #b9c0bb !important;
}

.training-plan__labels {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.training-plan__type {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid rgba(57, 196, 113, 0.25);
  border-radius: 999px;
  color: #77d99d;
  background: rgba(0, 150, 64, 0.1);
  font-size: 12px;
  font-weight: 750;
}

.training-plan__type--old {
  border-color: rgba(90, 166, 232, 0.26);
  color: #8ac2ef;
  background: rgba(58, 144, 222, 0.1);
}

.training-plan__type--deleted {
  border-color: rgba(239, 68, 68, 0.25);
  color: #f29a9a;
  background: rgba(239, 68, 68, 0.09);
}

.training-plan__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 15px;
  margin-top: 10px;
}

.training-plan__meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #a5ada7;
  font-size: 12px;
}

.training-plan__meta .v-icon {
  color: #909993 !important;
}

.training-plan__contributors {
  margin-top: 7px;
  color: #aeb5b0;
  font-size: 12px;
}

.training-plan__contributors span {
  color: #7f8982;
}

.training-plan__splits {
  display: grid;
  gap: 6px;
  margin-top: 13px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.training-split {
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.035);
}

.training-split > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.training-split strong,
.training-split span {
  font-size: 12px;
}

.training-split strong {
  color: #ecf1ed;
}

.training-split span {
  color: #8f9892;
}

.training-plan__actions {
  display: flex;
  max-width: 330px;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 3px;
}

.training-plan__actions .v-btn {
  min-height: 34px;
  margin: 0;
  border-radius: 9px;
  font-size: 12px;
}

.training-dialog-card {
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px !important;
  background: #1c201e !important;
}

.training-dialog-card__icon {
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  border-radius: 15px;
  color: #72d899;
  background: rgba(0, 150, 64, 0.13);
}

.training-dialog-card__icon--danger {
  color: #ff8585;
  background: rgba(239, 68, 68, 0.12);
}

.training-dialog-card__icon .v-icon {
  color: inherit !important;
}

.training-dialog-card h3 {
  margin: 0;
  color: #ffffff;
  font-size: 21px;
}

.training-dialog-card > p {
  margin: 8px 0 22px;
  color: #aeb5b0;
  font-size: 13px;
  line-height: 1.5;
}

.training-dialog-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}

.training-print-option {
  display: flex;
  width: 100%;
  min-height: 58px;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding: 10px 13px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.035);
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.training-print-option:hover {
  border-color: rgba(73, 203, 123, 0.35);
  background: rgba(0, 150, 64, 0.1);
}

.training-print-option > span {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.training-print-option strong,
.training-print-option small {
  font-size: 12px;
}

.training-print-option small {
  color: #9da59f;
}

.training-print-option .v-icon {
  color: #65ca8b !important;
}

@media (max-width: 850px) {
  .recent-courses__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .training-manager__header {
    align-items: stretch;
    flex-direction: column;
  }

  .training-manager__header-actions {
    justify-content: space-between;
  }

  .training-plan {
    align-items: flex-start;
    grid-template-columns: 42px minmax(0, 1fr);
  }

  .training-plan__icon {
    width: 42px;
    height: 42px;
  }

  .training-plan__actions {
    max-width: none;
    justify-content: flex-start;
    padding-top: 3px;
    grid-column: 2;
  }
}

@media (max-width: 600px) {
  .training-manager {
    padding: 16px 14px 2px;
  }

  .training-manager__header-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .recent-courses__header {
    align-items: flex-start;
  }

  .recent-courses__header h3 {
    font-size: 21px;
  }

  .recent-courses__grid {
    grid-template-columns: 1fr;
  }

  .recent-course {
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
  }

  .recent-course__image {
    height: 100%;
    min-height: 92px;
  }

  .training-manager__create,
  .training-manager__import {
    width: 100%;
  }

  .training-manager__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .training-plan {
    gap: 11px;
    padding: 15px 12px 15px 16px;
  }

  .training-plan__heading h4 {
    font-size: 15px;
  }

  .training-plan__actions .v-btn {
    padding: 0 7px;
  }
}
</style>
